import React, { useMemo, useState } from "react";
import "../../assets/styles/cards.css";
import { useViewCardsQuery } from "../../hooks/dashboard/cards.view.all.js";
import {
  MoveUp,
  Users,
  Share2,
  ShoppingBasket,
  MoveDown,
  Building2,
  MoreVertical,
  Edit2,
  Trash2,
} from "lucide-react";

const CARD_DEFINITIONS = [
  { key: "users", title: "Total Users", icon: Users },
  { key: "clients", title: "Total Clients", icon: Building2 },
  { key: "products", title: "Active Products", icon: ShoppingBasket },
  { key: "shares", title: "Total Shares", icon: Share2 },
];

const formatCount = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "0";
  return n.toLocaleString();
};

const formatTrend = (percentage) => {
  const n = Number(percentage) || 0;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n}%`;
};

const mapStatsToCards = (stats) => {
  if (!stats) return null;

  return CARD_DEFINITIONS.map(({ key, title, icon: Icon }) => {
    const item = stats[key] || {};
    const increasePercentage = Number(item.increasePercentage) || 0;

    return {
      title,
      value: formatCount(item.total),
      currentMonthAdded: formatCount(item.currentMonthAdded),
      lastMonthAdded: formatCount(item.lastMonthAdded),
      icon: <Icon size={25} />,
      trend: formatTrend(increasePercentage),
      description: "vs last month",
      isPositive: increasePercentage >= 0,
      meta: item,
    };
  });
};



const FALLBACK_CARDS = [
  {
    title: "Total Users",
    value: "—",
    currentMonthAdded: "—",
    lastMonthAdded: "—",
    icon: <Users size={25} />,
    trend: "—",
    description: "vs last month",
    isPositive: true,
  },
  {
    title: "Total Clients",
    value: "—",
    currentMonthAdded: "—",
    lastMonthAdded: "—",
    icon: <Building2 size={25} />,
    trend: "—",
    description: "vs last month",
    isPositive: true,
  },
  {
    title: "Active Products",
    value: "—",
    currentMonthAdded: "—",
    lastMonthAdded: "—",
    icon: <ShoppingBasket size={25} />,
    trend: "—",
    description: "vs last month",
    isPositive: true,
  },
  {
    title: "Total Shares",
    value: "—",
    currentMonthAdded: "—",
    lastMonthAdded: "—",
    icon: <Share2 size={25} />,
    trend: "—",
    description: "vs last month",
    isPositive: true,
  },
];

const Cards = ({ cardsData, onEdit }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const { data: fetchedData, isLoading, isError } = useViewCardsQuery();

  const dashboardCards = useMemo(() => {
    if (cardsData) return cardsData;

    const stats = fetchedData?.data || fetchedData;
    const fromApi = mapStatsToCards(stats);
    if (fromApi) return fromApi;

    return FALLBACK_CARDS;
  }, [cardsData, fetchedData]);

  return (
    <div className="cards-container">
      {isLoading && !cardsData && (
        <p style={{ color: "#666", fontSize: "13px", marginBottom: "12px" }}>
          Loading dashboard stats...
        </p>
      )}
      {isError && !cardsData && (
        <p style={{ color: "#d11a2a", fontSize: "13px", marginBottom: "12px" }}>
          Could not load dashboard stats.
        </p>
      )}

      <div className="card-body">
        {dashboardCards.map((card, index) => (
          <div
            className="cardItem"
            key={card.title || index}
            style={{
              background: card.bg || undefined,
              color: card.color || undefined,
            }}
          >
            <div className="card-content">
              <div className="card-left">
                <h3
                  className="card-title"
                  style={{ color: card.color || undefined }}
                >
                  {card.title}
                </h3>
                <p
                  className="card-value"
                  style={{ color: card.color || undefined }}
                >
                  {card.value}
                </p>

                {(card.currentMonthAdded != null ||
                  card.lastMonthAdded != null) && (
                  <div className="card-month-stats">
                    {card.currentMonthAdded != null && (
                      <div className="card-month-stat">
                        <span className="card-month-stat__label">
                          This month
                        </span>
                        <span
                          className="card-month-stat__value"
                          style={{ color: card.color || undefined }}
                        >
                          {card.currentMonthAdded}
                        </span>
                      </div>
                    )}
                    {card.lastMonthAdded != null && (
                      <div className="card-month-stat">
                        <span className="card-month-stat__label">
                          Last month
                        </span>
                        <span
                          className="card-month-stat__value"
                          style={{ color: card.color || undefined }}
                        >
                          {card.lastMonthAdded}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <p
                  className={`card-trend ${card.isPositive ? "positive" : "negative"}`}
                  style={{
                    color: card.color || undefined,
                    opacity: card.color ? 0.9 : undefined,
                  }}
                >
                  <span className="trend-icon">
                    {card.isPositive ? (
                      <MoveUp size={14} />
                    ) : (
                      <MoveDown size={14} />
                    )}
                  </span>
                  <span className="trend-text">{card.trend}</span>
                  <span className="trend-description">{card.description}</span>
                </p>
              </div>

              <div className="card-icon-wrap">
                {onEdit && (
                  <div className="card-menu">
                    <button
                      type="button"
                      className="card-menu-toggle"
                      onClick={() =>
                        setOpenMenu(openMenu === index ? null : index)
                      }
                    >
                      <MoreVertical size={20} />
                    </button>
                    {openMenu === index && (
                      <div className="card-menu-dropdown">
                        <button
                          type="button"
                          onClick={() => {
                            setOpenMenu(null);
                            onEdit(card);
                          }}
                        >
                          <Edit2 size={16} /> Edit
                        </button>
                        <button
                          type="button"
                          className="card-menu-delete"
                          onClick={() => setOpenMenu(null)}
                        >
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {card.icon && (
                  <div className="card-icon">
                    {card.icon}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Cards;
